import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Warning, UserCircle, Key } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function Account() {
  const { user, setUser, changePassword } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role && user.role !== "client";

  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    if (nw !== confirm) { toast.error("New passwords do not match."); return; }
    setPwBusy(true);
    const res = await changePassword(cur, nw);
    setPwBusy(false);
    if (res.ok) { toast.success("Password updated."); setCur(""); setNw(""); setConfirm(""); }
    else toast.error(res.error);
  };

  const deleteAccount = async () => {
    setDelBusy(true);
    try {
      await api.delete("/client/account");
      toast.success("Your account and personal data have been removed.");
      setUser(null);
      navigate("/login");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account.");
      setDelBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Settings" title="Account" description="Manage your account details, password and data." />

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 max-w-2xl mb-6">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
            <UserCircle size={32} weight="light" />
          </div>
          <div>
            <p className="font-heading text-2xl text-primary">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email} · {isStaff ? "Staff" : "Client"}</p>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 max-w-2xl mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Key size={20} weight="duotone" className="text-secondary" />
          <p className="eyebrow">Change password</p>
        </div>
        <form onSubmit={submitPw} className="space-y-4">
          <div><Label className="eyebrow">Current password</Label><Input type="password" className="mt-2" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" required /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="eyebrow">New password</Label><Input type="password" className="mt-2" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" required /></div>
            <div><Label className="eyebrow">Confirm new</Label><Input type="password" className="mt-2" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" required /></div>
          </div>
          <Button type="submit" disabled={pwBusy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="change-password-btn">
            {pwBusy ? "Updating…" : "Update password"}
          </Button>
        </form>
      </div>

      {/* Danger zone — clients only */}
      {!isStaff && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 sm:p-8 max-w-2xl">
          <div className="flex items-start gap-3">
            <Warning size={24} weight="fill" className="text-destructive shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-heading text-xl text-primary">Delete account</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Removes your account, personal data and private uploads, and revokes your access.
                Operational event records held by Gledswood are retained. Note: stored files are not
                yet physically erased by the storage provider.
              </p>
              <Button variant="destructive" className="rounded-full mt-4" onClick={() => { setConfirmText(""); setOpen(true); }} data-testid="delete-account-btn">
                Delete my account
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">Delete account?</DialogTitle>
            <DialogDescription>This removes your personal data and revokes access. Type DELETE to confirm.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="eyebrow">Type DELETE to confirm</Label>
            <Input className="mt-2" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" className="rounded-full" disabled={delBusy || confirmText !== "DELETE"} onClick={deleteAccount} data-testid="confirm-delete-account-btn">
              {delBusy ? "Deleting…" : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
